import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import axios from 'axios'
import { useAuthStore } from '../store/authStore'

const WEB_DEVICE_ID   = '00000000-0000-0000-0000-000000000001'
const WEB_DEVICE_NAME = 'Web Dashboard'

interface TokenResponse {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
  license_active: boolean
  license_expires_at: string | null
}

function decodeJwt(token: string): Record<string, unknown> {
  try {
    const payload = token.split('.')[1]
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
  } catch {
    return {}
  }
}

// ── Password strength ─────────────────────────────────────────────────────────

interface StrengthCheck { label: string; ok: boolean }

function passwordChecks(pw: string): StrengthCheck[] {
  return [
    { label: 'Minimalno 8 karaktera',     ok: pw.length >= 8 },
    { label: 'Veliko slovo (A–Z)',         ok: /[A-Z]/.test(pw) },
    { label: 'Malo slovo (a–z)',           ok: /[a-z]/.test(pw) },
    { label: 'Broj (0–9)',                 ok: /[0-9]/.test(pw) },
  ]
}

function PasswordStrength({ password }: { password: string }) {
  if (!password) return null
  const checks = passwordChecks(password)
  const passed = checks.filter((c) => c.ok).length
  const colors = ['bg-red-500', 'bg-orange-400', 'bg-yellow-400', 'bg-emerald-500']
  const barColor = colors[passed - 1] ?? 'bg-gray-300'
  return (
    <div className="space-y-1.5 mt-1">
      <div className="flex gap-1">
        {checks.map((_, i) => (
          <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i < passed ? barColor : 'bg-gray-200'}`} />
        ))}
      </div>
      <ul className="space-y-0.5">
        {checks.map((c) => (
          <li key={c.label} className={`flex items-center gap-1.5 text-xs ${c.ok ? 'text-emerald-600' : 'text-gray-400'}`}>
            <span>{c.ok ? '✓' : '○'}</span>
            {c.label}
          </li>
        ))}
      </ul>
    </div>
  )
}

// ── Field component ───────────────────────────────────────────────────────────

function Field({
  label, type = 'text', value, onChange, error, placeholder, autoComplete,
}: {
  label: string
  type?: string
  value: string
  onChange: (v: string) => void
  error?: string
  placeholder?: string
  autoComplete?: string
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
          error ? 'border-red-400 bg-red-50' : 'border-gray-300'
        }`}
      />
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ActivatePage() {
  const [params] = useSearchParams()
  const navigate  = useNavigate()
  const { setTokens, setUser } = useAuthStore()

  const token = params.get('token') ?? ''

  const [email,    setEmail]    = useState('')
  const [fullName, setFullName] = useState('')
  const [password, setPassword] = useState('')
  const [confirm,  setConfirm]  = useState('')
  const [loading,  setLoading]  = useState(false)
  const [prefilling, setPrefilling] = useState(!!token)
  const [success,  setSuccess]  = useState(false)
  const [apiError, setApiError] = useState<string | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Prefill from token info
  useEffect(() => {
    if (!token) return
    axios.get<{ email: string | null; full_name: string | null }>(
      `/api/v1/auth/token-info?token=${encodeURIComponent(token)}`
    )
      .then(({ data }) => {
        if (data.email)     setEmail(data.email)
        if (data.full_name) setFullName(data.full_name)
      })
      .catch(() => { /* prefill is best-effort */ })
      .finally(() => setPrefilling(false))
  }, [token])

  function validate(): boolean {
    const e: Record<string, string> = {}
    if (!email.trim())                   e.email    = 'E-mail je obavezan'
    else if (!/\S+@\S+\.\S+/.test(email)) e.email  = 'Neispravan e-mail format'
    if (!fullName.trim())                e.fullName = 'Ime je obavezno'
    const checks = passwordChecks(password)
    if (!checks[0].ok)                   e.password = 'Minimalno 8 karaktera'
    if (password !== confirm)            e.confirm  = 'Lozinke se ne poklapaju'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setApiError(null)
    if (!validate()) return

    setLoading(true)
    try {
      const { data } = await axios.post<TokenResponse>('/api/v1/auth/activate', {
        activation_token: token,
        email:            email.trim(),
        full_name:        fullName.trim(),
        password,
        device_id:        WEB_DEVICE_ID,
        platform:         'web',
        device_name:      WEB_DEVICE_NAME,
      })

      const payload = decodeJwt(data.access_token)
      const user = {
        id:       String(payload.sub ?? ''),
        email:    String(payload.email ?? email),
        fullName: String(payload.full_name ?? fullName),
        roles:    Array.isArray(payload.roles)   ? (payload.roles   as string[]) : [],
        modules:  Array.isArray(payload.modules) ? (payload.modules as string[]) : [],
        tenantId: String(payload.tenant_id ?? ''),
      }

      setTokens(data.access_token, data.refresh_token, data.license_active, data.license_expires_at)
      setUser(user)
      setSuccess(true)
      setTimeout(() => navigate('/'), 2000)
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setApiError(err.response?.data?.detail ?? 'Aktivacija nije uspela. Pokušajte ponovo.')
      } else {
        setApiError('Neočekivana greška.')
      }
    } finally {
      setLoading(false)
    }
  }

  if (!token) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 w-full max-w-sm text-center">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-lg font-bold text-gray-900 mb-2">Nevažeći link</h1>
          <p className="text-sm text-gray-500 mb-6">
            Ovaj link za aktivaciju je neispravan ili je istekao. Obratite se administratoru.
          </p>
          <Link to="/login" className="text-sm text-brand-cyan-text hover:text-brand-midnight hover:underline">Nazad na prijavu</Link>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 w-full max-w-sm text-center">
          <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-lg font-bold text-gray-900 mb-2">Nalog aktiviran!</h1>
          <p className="text-sm text-gray-500">Preusmervanje na dashboard…</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-slate-900 px-8 py-6 text-center">
          <div className="w-10 h-10 bg-brand-cyan rounded-xl flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-brand-midnight" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M12 3c-4.97 5.38-7 8.87-7 12a7 7 0 0014 0c0-3.13-2.03-6.62-7-12z" />
            </svg>
          </div>
          <h1 className="text-white text-xl font-bold">I-SOFT Waterworks</h1>
          <p className="text-slate-400 text-sm mt-1">Aktivacija naloga</p>
        </div>

        {/* Form */}
        {prefilling ? (
          <div className="px-8 py-10 flex justify-center">
            <div className="w-6 h-6 border-4 border-brand-cyan border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-8 py-6 space-y-4">
            <p className="text-sm text-gray-500">
              Podesite podatke za Vaš nalog. Nakon aktivacije, prijavite se sa zadatim e-mailom i lozinkom.
            </p>

            <Field
              label="E-mail"
              type="email"
              value={email}
              onChange={setEmail}
              error={errors.email}
              placeholder="vase@email.com"
              autoComplete="email"
            />
            <Field
              label="Ime i prezime"
              value={fullName}
              onChange={setFullName}
              error={errors.fullName}
              placeholder="Ime Prezime"
              autoComplete="name"
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lozinka</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Unesite lozinku"
                autoComplete="new-password"
                className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                  errors.password ? 'border-red-400 bg-red-50' : 'border-gray-300'
                }`}
              />
              {errors.password && <p className="mt-1 text-xs text-red-500">{errors.password}</p>}
              <PasswordStrength password={password} />
            </div>

            <Field
              label="Potvrdite lozinku"
              type="password"
              value={confirm}
              onChange={setConfirm}
              error={errors.confirm}
              autoComplete="new-password"
            />

            {apiError && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                {apiError}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 text-sm font-semibold rounded-lg bg-brand-cyan text-brand-midnight hover:bg-brand-cyan-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Aktivacija u toku…' : 'Aktiviraj nalog'}
            </button>

            <p className="text-center text-xs text-gray-400">
              Već imate nalog?{' '}
              <Link to="/login" className="text-brand-cyan-text hover:text-brand-midnight hover:underline">Prijavite se</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  )
}
